/**
 * Reglas puras del dominio Pedidos/Operaciones (O8 hardening).
 * Lógica sin efectos secundarios, testeable, usada por la capa de datos y la UI.
 * Los totales/pagos son SERVER-AUTHORITATIVE (triggers); estas funciones reflejan
 * las mismas reglas para inferencia local, UI optimista y derivados de dashboard.
 */

export type ConfirmMode = 'manual' | 'auto_on_payment';
export type PaymentKind = 'advance' | 'balance' | 'full';

/** Default de confirmación por canal (coincide con fn_confirm_mode en la BD). */
export function defaultConfirmMode(channel: string): ConfirmMode {
  return channel === 'online_checkout' ? 'auto_on_payment' : 'manual';
}

/**
 * Infiere el tipo de cobro según lo ya pagado:
 * - nada pagado y cubre el total → 'full'
 * - nada pagado y parcial → 'advance'
 * - ya había pagos → 'balance'
 */
export function inferPaymentKind(alreadyPaid: number, amount: number, grandTotal: number): PaymentKind {
  if (alreadyPaid <= 0) return amount >= grandTotal ? 'full' : 'advance';
  return 'balance';
}

/** Sanea el término de búsqueda para `.or(... ilike ...)` de PostgREST. */
export function sanitizeSearch(q: string): string {
  return (q || '').trim().replace(/[,()%*]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Derivados del dashboard: ticket promedio y tasa de cobranza (%). Sin división por cero. */
export function dashboardDerived(d: { sales_count: number; revenue: number; collected: number }): {
  aov: number;
  collectionRate: number;
} {
  const aov = d.sales_count > 0 ? d.revenue / d.sales_count : 0;
  const collectionRate = d.revenue > 0 ? (d.collected / d.revenue) * 100 : 0;
  return { aov, collectionRate };
}

/** Fecha relativa en español para listas (hoy/ayer/hace N días/fecha). */
/** Fecha de HOY en zona local como YYYY-MM-DD (NO usar toISOString, que da la fecha UTC
 *  y de noche en zonas con offset negativo devuelve "mañana"). */
export function todayLocalDate(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function relativeDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const daysDiff = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (daysDiff === 0) return `hoy ${hhmm}`;
  if (daysDiff === 1) return `ayer ${hhmm}`;
  if (daysDiff > 1 && daysDiff < 7) return `hace ${daysDiff} días`;
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Fase R: roles y capacidades de Operaciones (RBAC) ───────────────────────
export type OpsRole = 'admin' | 'ventas' | 'logistica' | 'contabilidad';

export interface OpsCaps {
  viewPrices: boolean;   // ver precios/totales/márgenes
  editOrder: boolean;    // editar líneas/cargos, confirmar/cancelar
  collect: boolean;      // registrar cobros/reembolsos, cobranza
  logistics: boolean;    // envíos, tracking, imprimir guía/rótulo
  billing: boolean;      // emitir boleta/factura
}

/** Capacidades por rol. Modelo explícito (cada rol ve/hace solo lo suyo). */
export function opsCaps(role: OpsRole): OpsCaps {
  switch (role) {
    case 'logistica':
      return { viewPrices: false, editOrder: false, collect: false, logistics: true, billing: false };
    case 'contabilidad':
      return { viewPrices: true, editOrder: false, collect: false, logistics: false, billing: true };
    case 'ventas':
      return { viewPrices: true, editOrder: true, collect: true, logistics: true, billing: false };
    case 'admin':
    default:
      return { viewPrices: true, editOrder: true, collect: true, logistics: true, billing: true };
  }
}

export const OPS_ROLE_LABEL: Record<OpsRole, string> = {
  admin: 'Administrador', ventas: 'Ventas / Asesor', logistica: 'Logística', contabilidad: 'Contabilidad',
};

export type OpsQueue = 'pedidos' | 'ventas' | 'logistica' | 'cobranzas' | 'canceladas';

/** Colas (listas) que ve cada rol en el menú. El detalle igual es role-aware. */
export function opsVisibleQueues(role: OpsRole): OpsQueue[] {
  switch (role) {
    case 'logistica':
      return ['logistica'];
    case 'contabilidad':
      return ['ventas', 'cobranzas', 'canceladas'];
    case 'ventas':
      return ['pedidos', 'ventas', 'cobranzas', 'canceladas'];
    case 'admin':
    default:
      return ['pedidos', 'ventas', 'logistica', 'cobranzas', 'canceladas'];
  }
}

export type DeliveryRisk = 'low' | 'medium' | 'high' | 'unknown';

/**
 * Riesgo de entrega COD según el histórico del cliente: entregados vs fallidos
 * (cancelados / devueltos). Sin historial → 'unknown'. Modelo simple y explicable;
 * se puede reemplazar por scoring ML más adelante.
 */
export function deliveryRisk(delivered: number, failed: number): DeliveryRisk {
  const total = delivered + failed;
  if (total === 0) return 'unknown';
  const rate = delivered / total;
  if (rate >= 0.8) return 'low';
  if (rate >= 0.5) return 'medium';
  return 'high';
}
